import { PortalQuoteResponseType } from 'stringline-types';
import { formatCurrency, formatUnit } from '../lib/format';

type QuoteData = PortalQuoteResponseType['data'];

interface LineItemsProps {
  lineItems: QuoteData['lineItems'];
  totals: QuoteData['totals'];
  pricesIncludeTax: boolean;
  isPending: boolean;
  selectedLineItemIds: Set<string>;
  onToggleItem: (id: string) => void;
  currency: string;
  locale: string;
}

const LineItems = ({
  lineItems,
  totals,
  pricesIncludeTax,
  isPending,
  selectedLineItemIds,
  onToggleItem,
  currency,
  locale,
}: LineItemsProps) => {
  const money = (cents: number) => formatCurrency(cents, currency, locale);
  const dynamicSubtotalCents = lineItems.reduce((sum, item) => {
    if (!selectedLineItemIds.has(item.id)) return sum;
    return sum + item.lineTotalCents;
  }, 0);

  const displaySubtotal = isPending ? dynamicSubtotalCents : totals.subtotalCents;

  let displayTax: number;
  let displayTotal: number;
  if (!isPending) {
    displayTax = totals.taxCents;
    displayTotal = totals.totalIncTaxCents;
  } else if (pricesIncludeTax && totals.subtotalCents > 0) {
    const inclusiveRatio = totals.taxCents / totals.subtotalCents;
    displayTax = Math.round(dynamicSubtotalCents * inclusiveRatio);
    displayTotal = dynamicSubtotalCents;
  } else if (!pricesIncludeTax && totals.totalExTaxCents > 0) {
    const taxRate = totals.taxCents / totals.totalExTaxCents;
    displayTax = Math.round(dynamicSubtotalCents * taxRate);
    displayTotal = dynamicSubtotalCents + displayTax;
  } else {
    displayTax = 0;
    displayTotal = dynamicSubtotalCents;
  }

  return (
    <section aria-label="Line items">
      <h2 className="text-base font-semibold text-slate-900">Items</h2>
      <p className="mt-1 text-xs text-slate-500">
        Prices are {pricesIncludeTax ? 'inclusive' : 'exclusive'} of tax.
      </p>

      {isPending ? (
        <p className="mt-1 text-xs text-slate-500">
          Select or deselect optional items to include or exclude items from your quote total.
        </p>
      ) : null}

      <div className="mt-3 overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
              {isPending ? <th scope="col" className="py-2 pr-2 w-8" /> : null}
              <th scope="col" className="py-2 pr-3">#</th>
              <th scope="col" className="py-2 pr-3">Item</th>
              <th scope="col" className="py-2 pr-3 text-right">Qty</th>
              <th scope="col" className="py-2 pr-3 text-right">Unit price</th>
              <th scope="col" className="py-2 pl-3 text-right">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {lineItems.map((item) => {
              const isSelected = selectedLineItemIds.has(item.id);
              const rowOpacity = isPending && !isSelected ? 'opacity-50' : '';

              return (
                <tr key={item.id} className={`align-top ${rowOpacity}`}>
                  {isPending ? (
                    <td className="py-3 pr-2">
                      {item.isOptional ? (
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => onToggleItem(item.id)}
                          aria-label={`Include ${item.name}`}
                          className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                        />
                      ) : null}
                    </td>
                  ) : null}
                  <td className="py-3 pr-3 text-slate-500">{item.lineNumber}</td>
                  <td className="py-3 pr-3">
                    <div className="font-medium text-slate-900">{item.name}</div>
                    {item.notes ? (
                      <div className="mt-1 whitespace-pre-line text-xs text-slate-500">{item.notes}</div>
                    ) : null}
                    {item.isOptional ? (
                      <span className="mt-1 inline-block rounded bg-amber-50 px-1.5 py-0.5 text-xs font-medium text-amber-700">
                        Optional
                      </span>
                    ) : null}
                  </td>
                  <td className="py-3 pr-3 text-right text-slate-700">
                    {item.quantity} {formatUnit(item.unit, item.quantity)}
                  </td>
                  <td className="py-3 pr-3 text-right tabular-nums text-slate-700">
                    {money(item.unitPriceCents)}
                  </td>
                  <td className="py-3 pl-3 text-right tabular-nums font-medium text-slate-900">
                    {money(item.lineTotalCents)}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="text-sm">
            <tr>
              <td colSpan={isPending ? 4 : 3} />
              <td className="py-2 pr-3 text-right text-slate-500">Subtotal</td>
              <td className="py-2 pl-3 text-right tabular-nums text-slate-700">
                {money(displaySubtotal)}
              </td>
            </tr>
            <tr>
              <td colSpan={isPending ? 4 : 3} />
              <td className="py-2 pr-3 text-right text-slate-500">Tax</td>
              <td className="py-2 pl-3 text-right tabular-nums text-slate-700">
                {money(displayTax)}
              </td>
            </tr>
            <tr className="border-t border-slate-200">
              <td colSpan={isPending ? 4 : 3} />
              <td className="pt-3 pr-3 text-right text-base font-semibold text-slate-900">Total (inc tax)</td>
              <td className="pt-3 pl-3 text-right text-base font-semibold tabular-nums text-slate-900">
                {money(displayTotal)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
};

export default LineItems;
